import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import OTPInput from "otp-input-react";
import { doc, onSnapshot } from "firebase/firestore";

import { AuthContainer } from "shared";
import { useRegister } from "api";
import { Button } from "components";
import { formatTime } from "utils";
import { RadioInput } from "components/Input";
import { db } from "api/lib";

const VERIFICATION_CHANNELS = [
  { label: "sms", value: "sms" },
  { label: "whatsapp", value: "whatsapp" },
];

const OTP_LENGTH = 6;
const EMAIL_TIMER = 60;
const PHONE_TIMER = 180;

const VerificationSection = ({
  title,
  isVerified,
  otp,
  onOtpChange,
  onResend,
  onVerify,
  onSendCode,
  isResendDisabled,
  showSendButton,
  counter,
  isLoading,
  recipientInfo,
  children,
}) => (
  <div className="my-2 w-[90%] rounded-sm bg-gray-100/60 px-3 pt-3 pb-5 shadow-sm">
    <p className="font-bold capitalize">{title}</p>
    {isVerified ? (
      <span className="font-semibold text-green-600">{title} verified</span>
    ) : (
      <>
        <p className="pt-1 pb-2 text-xs">
          {showSendButton
            ? `Click "Send Code" to receive verification code at ${recipientInfo}`
            : `Enter the verification code sent to ${recipientInfo}`}
        </p>
        {children}

        {showSendButton ? (
          <div className="mt-3">
            <Button
              label="Send Code"
              gradient
              handleClick={onSendCode}
              isSubmitting={isLoading}
            />
          </div>
        ) : (
          <>
            <OTPInput
              value={otp}
              onChange={onOtpChange}
              OTPLength={OTP_LENGTH}
              otpType="number"
              disabled={false}
              inputClassName="border rounded-md border-gray-300 text-gray-500 !mr-1 !h-10 !w-10 focus:border-color-main outline-none focus:border-2 focus: rounded-sm"
            />
            <div className="my-1">
              <button
                className="text-xs text-color-main"
                onClick={onResend}
                disabled={isResendDisabled}
              >
                {counter <= 1 ? (
                  <>Didn't receive code? Resend</>
                ) : (
                  <>Retry send code - {formatTime(counter)}s</>
                )}
              </button>
            </div>
            <div className="mt-6">
              <Button
                label={`Verify ${title.toLowerCase()}`}
                gradient
                handleClick={onVerify}
                disabled={otp.length < OTP_LENGTH}
                isSubmitting={isLoading}
              />
            </div>
          </>
        )}
      </>
    )}
  </div>
);

const RegisterOTP = () => {
  const [emailOtp, setEmailOtp] = useState("");
  const [phoneOtp, setPhoneOtp] = useState("");
  const [emailCounter, setEmailCounter] = useState(0);
  const [phoneCounter, setPhoneCounter] = useState(0);
  const [messageChannel, setMessageChannel] = useState("sms");
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [firestoreData, setFirestoreData] = useState(null);
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);

  const [
    isLoading,
    isLoaded,
    register,
    sendEmailCodeStatus,
    setSendEmailCodeStatus,
    sendPhoneCodeStatus,
    setSendPhoneCodeStatus,
    phoneOTPRef,
    resendEmailOtpCode,
    resendPhoneOtpCode,
    sendPhoneOtpCode,
    verifyEmailOTP,
    verifyPhoneOTP,
  ] = useRegister();

  const { state } = useLocation();
  const navigate = useNavigate();

  // Redirect if no state
  useEffect(() => {
    if (!state) {
      navigate("/register", { replace: true });
    }
  }, [navigate, state]);

  // Subscribe to Firestore document
  useEffect(() => {
    if (!state?.userDetails.email) return;

    const docRef = doc(
      db,
      "userRegVerificationStatus",
      state?.userDetails.email
    );

    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();

          // console.log(data, "realtime firestore data");
          setFirestoreData(data);
          setEmailVerified(data.emailVerified || false);
          setPhoneVerified(data.phoneVerified || false);
        }
      },
      (error) => {
        console.error("Error listening to Firestore:", error);
      }
    );

    return () => unsubscribe();
  }, [state?.userDetails.email]);

  // Timer hook
  useEffect(() => {
    const startTimer = (isActive, duration, setCounter, setStatus) => {
      if (!isActive) return;

      let timeLeft = duration;
      const interval = setInterval(() => {
        if (timeLeft <= 0) {
          clearInterval(interval);
          setStatus(false);
        } else {
          setCounter(timeLeft);
        }
        timeLeft -= 1;
      }, 1000);

      return () => clearInterval(interval);
    };

    const emailCleanup = startTimer(
      sendEmailCodeStatus,
      EMAIL_TIMER,
      setEmailCounter,
      setSendEmailCodeStatus
    );
    const phoneCleanup = startTimer(
      sendPhoneCodeStatus,
      PHONE_TIMER,
      setPhoneCounter,
      setSendPhoneCodeStatus
    );

    return () => {
      emailCleanup?.();
      phoneCleanup?.();
    };
  }, [
    sendEmailCodeStatus,
    sendPhoneCodeStatus,
    setSendEmailCodeStatus,
    setSendPhoneCodeStatus,
  ]);

  const handleSendEmailCode = () => {
    resendEmailOtpCode({
      email: state?.userDetails.email,
      displayName: state?.userDetails.displayName,
    });
    setSendEmailCodeStatus(true);
    setEmailCodeSent(true);
  };

  const handleResendEmail = () => {
    resendEmailOtpCode({
      email: state?.userDetails.email,
      displayName: state?.userDetails.displayName,
    });
    setSendEmailCodeStatus(true);
  };

  const handleSendPhoneCode = () => {
    sendPhoneOtpCode({
      values: state.userDetails,
      messageChannel,
    });
    setPhoneCodeSent(true);
    setSendPhoneCodeStatus(true);
  };

  const handleResendPhone = () => {
    resendPhoneOtpCode(state);
    setSendPhoneCodeStatus(true);
  };

  const handleVerifyEmail = async () => {
    const result = await verifyEmailOTP({
      email: state?.userDetails.email,
      code: emailOtp,
    });

    // console.log(result, "result");
  };

  const handleVerifyPhone = async () => {
    await verifyPhoneOTP({
      phoneNumber: state?.userDetails.phoneNumber,
      code: phoneOtp,
      phoneOTPRef: phoneOTPRef,
      email: state?.userDetails.email,
    });
  };

  const handleRegister = () => {
    register({
      values: state.userDetails,
      referralParam:
        state.marketingReferral || state.bitMediaReferral || "organic",
    });
  };

  return (
    <AuthContainer
      title="Account Verification"
      subTitle="We'll like to verify you! This will only take a few minutes."
    >
      <div>
        <VerificationSection
          title="Email"
          isVerified={emailVerified}
          otp={emailOtp}
          onOtpChange={setEmailOtp}
          onSendCode={handleSendEmailCode}
          onResend={handleResendEmail}
          onVerify={handleVerifyEmail}
          isResendDisabled={sendEmailCodeStatus}
          showSendButton={!emailCodeSent}
          counter={emailCounter}
          isLoading={isLoading}
          recipientInfo={state?.userDetails.email}
        />

        <VerificationSection
          title="Phone Number"
          isVerified={phoneVerified}
          otp={phoneOtp}
          onOtpChange={setPhoneOtp}
          onSendCode={handleSendPhoneCode}
          onResend={handleResendPhone}
          onVerify={handleVerifyPhone}
          isResendDisabled={sendPhoneCodeStatus}
          showSendButton={!phoneCodeSent}
          counter={phoneCounter}
          isLoading={isLoading}
          recipientInfo={`+${state?.userDetails.phoneNumber}`}
        ></VerificationSection>

        <div className="mt-6">
          <Button
            label="Register"
            gradient
            handleClick={handleRegister}
            disabled={!emailVerified || !phoneVerified}
            isSubmitting={isLoading}
          />
        </div>
      </div>
    </AuthContainer>
  );
};

export default RegisterOTP;
